/**
 * When each lead was last touched, from the activity log itself.
 *
 * Decision 102 puts the timestamp of the last activity-log entry on the
 * distribution board's cards. It is deliberately **not** `updatedAt`: that moves
 * whenever anybody edits any field, so a board sorted or read by it would report
 * a corrected postcode as contact. The log is the record of what happened to the
 * lead, and its newest entry is the answer.
 *
 * ## Why this reads the whole collection
 *
 * Firestore cannot return "the newest row per group", so the alternative to
 * reading the log is denormalising a `lastActivityAt` onto every lead — a field
 * to write on every path that logs, a backfill for the 555 leads that already
 * exist, and a new way for two numbers to disagree.
 *
 * The log is 937 documents. One subscription over it costs a small first read
 * and then only the changes, which is also what keeps a card's timestamp correct
 * the moment a drag writes "Owner changed to …" — the board and the drawer read
 * the same source, which is the whole point of 102's last clause.
 *
 * If the log outgrows this — tens of thousands — the answer is the stamped field
 * and its backfill, not a bigger read. It is worth saying so here so the next
 * person does not have to work out why this was ever acceptable.
 */
import { useEffect, useState } from 'react'
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore'
import { db } from '@/shared/firebase/client'
import { CRM_COLLECTIONS } from '@/shared/firebase/collections'
import { toIsoOrNull } from '@/shared/util/dates'

/** `leadId` → ISO timestamp of its newest activity. Absent means none logged. */
export function useLastActivity(): Map<string, string> {
  const [byLead, setByLead] = useState<Map<string, string>>(new Map())

  useEffect(() => {
    // Newest first, so the first row seen for a lead is its latest.
    const q = query(collection(db, CRM_COLLECTIONS.activities), orderBy('at', 'desc'))
    return onSnapshot(
      q,
      (snap) => {
        const out = new Map<string, string>()
        for (const d of snap.docs) {
          const leadId = String(d.get('leadId') ?? '')
          if (!leadId || out.has(leadId)) continue
          const at = toIsoOrNull(d.get('at'))
          if (at) out.set(leadId, at)
        }
        setByLead(out)
      },
      () => {
        // A refused read leaves the cards without a timestamp, which the card
        // renders as an em dash. It is not worth breaking the board over.
        setByLead(new Map())
      },
    )
  }, [])

  return byLead
}
